import styled from 'styled-components'
import SelectItemFilter from './viewSelectModalVariable.filters.select.component'
import {
  Expression,
  ExpressionBinaryOperator,
  ExpressionOperationAvailable,
} from '~/prix/types/expression'
import { memo, useEffect, useLayoutEffect, useMemo, useState } from 'react'
import InputItemFilter from './viewSelectModalVariable.filters.input.component'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrash } from '@fortawesome/free-solid-svg-icons'
import { GenericObject, NativeBaseOptional } from '~/prix/types/type'
import SelectAgentFilter from './viewSelectModalVariable.filters.agent.component'
import SelectRegionalFilter from './viewSelectModalVariable.filters.regional.component'

const Container = styled.div`
  display: flex;
  margin: 0px 0px 8px 0px;

  h3 {
    font-size: 0.8rem;
    color: ${({ theme }) => theme.colors.darkGrey};
    font-weight: 600;
  }
`
const FiltersBox = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  gap: 1rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}px) {
    flex-direction: column;
  }
`

const ItemsBox = styled.div`
  display: flex;
  gap: 0.3rem;
  height: 32px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}px) {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    width: 100%;
    min-height: 32px;
    height: unset;
  }
`

const WrapperDeleteButton = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}px) {
    align-items: flex-start;
    margin-top: 0.5rem;
  }

  button {
    border: none;
    background-color: transparent;
    cursor: pointer;
  }
`

const TwoInputsBox = styled.div`
  display: flex;
  flex-direction: row;
  gap: 0.5rem;
  justify-content: center;
  align-items: center;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}px) {
    flex-direction: column;
    gap: 0.3rem;
    align-items: flex-start;
    width: 100%;

    span {
      align-self: center;
      font-size: 0.9rem;
    }
  }
`

interface FiltersComponentProps {
  possibleProperties: FilteredType[]
  isLoading: boolean
  filtersApplied?: Expression
  expressionIndex: string
  setNewExpression: (expression: Expression, index: number) => void
  removeExpression: (index: string) => void
  resizeHeight: () => void
}

export interface FilteredType {
  value: GenericObject
  label?: string
}

type BinaryExpression = {
  type: 'expression'
  kind: 'binary'
  operator: ExpressionBinaryOperator
  left: Expression
  right: Expression
}

const FiltersComponent = memo(
  ({
    possibleProperties,
    isLoading,
    filtersApplied,
    expressionIndex,
    setNewExpression,
    removeExpression,
    resizeHeight,
  }: FiltersComponentProps) => {
    const [expression, setExpression] = useState<Expression>({} as Expression)
    const [selectCurrentProperty, setSelectCurrentProperty] = useState<Expression[]>([])
    const [selectExpressionOptions, setSelectExpressionOptions] = useState<FilteredType[]>()
    const [labels, setLabels] = useState<string[]>([])
    const numberOfOptions = useMemo(() => {
      return Object.keys(expression).length + 1
    }, [expression])

    const alreadyHasExpression = useMemo(() => {
      if (filtersApplied?.kind === 'binary' && filtersApplied?.right?.kind) {
        const isComplete = Object.keys(filtersApplied?.right)?.length > 0
        return isComplete
      }
    }, [filtersApplied])

    useLayoutEffect(() => {
      resizeHeight()
    }, [expression])

    useEffect(() => {
      if (filtersApplied && alreadyHasExpression && filtersApplied.kind === 'binary') {
        const current = [
          { kind: 'binary', type: 'expression', left: filtersApplied.left },
          { kind: 'binary', type: 'expression', operator: filtersApplied.operator },
          { kind: 'binary', type: 'expression', right: filtersApplied.right },
        ]
        getLabels(filtersApplied)
        setExpression(filtersApplied)
        setSelectCurrentProperty(current as Expression[])
      } else {
        setLabels([])
      }
    }, [filtersApplied, alreadyHasExpression])

    useEffect(() => {
      if (Object.keys(expression).length <= 0 && !alreadyHasExpression) {
        setSelectExpressionOptions(possibleProperties)
        setSelectCurrentProperty([
          { kind: 'binary', type: 'expression', left: { kind: 'property' } } as Expression,
        ])
      }
    }, [expression])

    const getLabels = (currentExpressionSelected: Expression) => {
      if (currentExpressionSelected.kind !== 'binary') return
      if (currentExpressionSelected.left.kind !== 'property') return
      const leftExpression = currentExpressionSelected.left as Expression
      if (leftExpression.kind !== 'property') return
      const left = possibleProperties?.filter(property => {
        if ('key' in property.value) {
          return property.value.key === leftExpression.property
        }
        return false
      })[0]
      const possibleOperators = checkOperationsAvailable(
        left?.value?.type as 'string' | 'number' | 'boolean',
        left?.value?.key as string | undefined,
      )
      const leftLabel = left?.label
      const operatorLabel = possibleOperators.filter(
        property => property?.value?.key === currentExpressionSelected?.operator,
      )[0]?.label
      setLabels([leftLabel as string, operatorLabel])
      if (
        currentExpressionSelected.right.kind === 'literal' &&
        currentExpressionSelected.right.subType === 'enumerated'
      ) {
        const right = possibleProperties?.filter(property => {
          return property.value.key === leftExpression.property
        })[0]
        const rightKey = currentExpressionSelected?.right?.value
        const enumeratedLabels = right?.value?.keysAndLabels as GenericObject
        const rightLabel = enumeratedLabels[rightKey as string]
        setLabels(prev => [...prev, rightLabel as string])
      }
      if (
        currentExpressionSelected.right.kind === 'literal' &&
        currentExpressionSelected.right.subType === 'boolean'
      ) {
        const rightKey = currentExpressionSelected?.right?.value
        const booleanLabels = getBooleanLabels()
        const rightLabel = booleanLabels?.find(
          option => option?.value?.key === rightKey.toString(),
        )?.label
        setLabels(prev => [...prev, rightLabel as string])
      }
    }

    const handleNewElementoToExpression = (element: FilteredType, isBetween?: boolean) => {
      if (Object.keys(expression).length <= 0) {
        const elementToBeAdded = {
          kind: 'binary',
          type: 'expression',
          left: {
            kind: 'property',
            type: 'expression',
            property: element.value.key,
            literalType: element.value.type,
            subType: element.value.subType,
            label: element.label,
          },
        }
        setExpression(elementToBeAdded as Expression)
        const updatedCurrent = [elementToBeAdded as Expression, ...selectCurrentProperty.slice(1)]
        setSelectCurrentProperty(updatedCurrent)
        checkOperationsAvailable(
          element.value.type as 'string' | 'number' | 'boolean',
          element.value.key as string | undefined,
        )
        return
      }
      if (element.value.kind === 'operator') {
        const newExpression = {
          ...expression,
          operator: element.value.key,
          kind: 'binary',
        } as Expression
        setExpression(newExpression)
        const updatedCurrent = [
          ...selectCurrentProperty.slice(0, selectCurrentProperty.length - 1),
          { operator: element.value.key },
        ]
        setSelectCurrentProperty(updatedCurrent as Expression[])
        if (newExpression.kind === 'binary' && newExpression.left.kind === 'property') {
          setSelectExpressionOptions([])
          checkPossibilityAfterOperator(
            newExpression.left.literalType,
            element.value.key as 'string',
            newExpression.left.subType,
            newExpression.left.property,
          )
        }
        return
      }
      if (isBetween) {
        const isBinaryExpression = (expr: Expression): expr is BinaryExpression =>
          expr.kind === 'binary' && 'right' in expr && 'left' in expr
        const newExpression = {
          ...expression,
          right: {
            operator: 'AND',
            ...(isBinaryExpression(expression) ? expression.right : {}),
            ...(isBinaryExpression(expression) ? {} : { kind: 'binary' }),
            ...(isBinaryExpression(expression) ? {} : { type: 'expression' }),
            ...element.value,
          },
        }
        setExpression(newExpression as Expression)
        const updatedCurrent = [
          ...selectCurrentProperty.slice(0, selectCurrentProperty.length - 1),
          { kind: 'binary', type: 'expression', right: newExpression.right },
        ]
        setSelectCurrentProperty(updatedCurrent as Expression[])
        if (
          newExpression.kind === 'binary' &&
          newExpression.right.kind === 'binary' &&
          'right' in newExpression.right &&
          'left' in newExpression.right &&
          newExpression?.right?.right &&
          newExpression?.right?.left
        ) {
          setNewExpression(newExpression as Expression, Number(expressionIndex))
        }
      }

      if (element.value.kind === 'boolean') {
        const newExpression = {
          ...expression,
          right: {
            kind: 'literal',
            type: 'expression',
            literalType: element?.value?.type,
            value: element?.value?.key,
            subType: element?.value?.type,
          },
        } as Expression
        setExpression(newExpression)
        const updatedCurrent = [
          ...selectCurrentProperty.slice(0, selectCurrentProperty.length - 1),
          {
            kind: 'binary',
            type: 'expression',
            right: {
              kind: 'literal',
              type: 'expression',
              literalType: element?.value?.literalType,
              value: element?.value?.key,
            },
          },
        ]

        setSelectCurrentProperty(updatedCurrent as Expression[])
        setNewExpression(newExpression, Number(expressionIndex))
        return
      }
      if (element.value.kind === 'enumerated') {
        const newExpression = {
          ...expression,
          right: {
            kind: 'literal',
            type: 'expression',
            subType: element?.value?.kind,
            literalType: element?.value?.literalType ?? element?.value?.type,
            value: element?.value?.key,
          },
        } as Expression
        setExpression(newExpression)
        const updatedCurrent = [
          ...selectCurrentProperty.slice(0, selectCurrentProperty.length - 1),
          {
            kind: 'binary',
            type: 'expression',
            right: {
              kind: 'enumerated',
              type: 'expression',
              literalType: 'string',
              value: element?.value?.key,
            },
          },
        ]
        setSelectCurrentProperty(updatedCurrent as Expression[])
        setNewExpression(newExpression, Number(expressionIndex))
        return
      }
      if (element.value.kind === 'literal') {
        const newExpression = {
          ...expression,
          right: {
            kind: 'literal',
            type: 'expression',
            literalType: element.value.literalType ?? element.value.type,
            value: element.value.value,
            label: element.value.label || undefined,
          },
        } as Expression
        setExpression(newExpression)
        const updatedCurrent = [
          ...selectCurrentProperty.slice(0, selectCurrentProperty.length - 1),
          {
            kind: 'literal',
            type: 'expression',
            right: { ...element.value, label: element.label || undefined },
          },
        ]
        setSelectCurrentProperty(updatedCurrent as Expression[])
        setNewExpression(newExpression, Number(expressionIndex))
        return
      }
    }

    const checkPossibilityAfterOperator = (
      typeOfElement: 'number' | 'string' | 'boolean' | undefined,
      element?: string,
      subType?: NativeBaseOptional,
      fieldName?: string,
    ) => {
      if (typeOfElement === 'boolean') {
        setSelectCurrentProperty(
          prev =>
            [
              ...prev,
              {
                kind: 'binary',
                type: 'expression',
                right: {
                  kind: 'literal',
                  type: 'expression',
                  literalType: typeOfElement,
                  subType: typeOfElement,
                },
              },
            ] as Expression[],
        )
        setSelectExpressionOptions(getBooleanLabels())

        return
      }
      if (subType === 'enumerated') {
        setSelectCurrentProperty(
          prev =>
            [
              ...prev,
              {
                kind: 'binary',
                type: 'expression',
                right: {
                  kind: 'literal',
                  type: 'expression',
                  literalType: typeOfElement,
                  subType: subType,
                },
              },
            ] as Expression[],
        )
        getEnumeratedLabels(expression)
        return
      }
      if (fieldName === 'agentCpf' || fieldName === 'agentPolygonCpf') {
        setSelectCurrentProperty(
          prev =>
            [
              ...prev,
              {
                kind: 'binary',
                type: 'expression',
                right: {
                  kind: 'literal',
                  type: 'expression',
                  literalType: 'string',
                  subType: 'agentSelect',
                },
              },
            ] as Expression[],
        )
        return
      }
      if (fieldName === 'regional' || fieldName === 'attendanceData.regional') {
        setSelectCurrentProperty(
          prev =>
            [
              ...prev,
              {
                kind: 'binary',
                type: 'expression',
                right: {
                  kind: 'literal',
                  type: 'expression',
                  literalType: 'string',
                  subType: 'regionalSelect',
                },
              },
            ] as Expression[],
        )
        return
      }
      if (element === 'BETWEEN' || element === 'NOT BETWEEN') {
        setSelectCurrentProperty(
          prev =>
            [
              ...prev,
              {
                kind: 'binary',
                type: 'expression',
                right: {
                  kind: 'binary',
                  type: 'expression',
                  operator: 'AND',
                  left: {
                    kind: 'literal',
                    type: 'expression',
                    literalType: typeOfElement,
                  },
                  right: {
                    kind: 'literal',
                    type: 'expression',
                    literalType: typeOfElement,
                  },
                },
              },
            ] as Expression[],
        )
        return
      }
      setSelectCurrentProperty(
        prev =>
          [
            ...prev,
            {
              kind: 'binary',
              type: 'expression',
              right: { literalType: typeOfElement, kind: 'literal', type: 'expression' },
            },
          ] as Expression[],
      )
    }

    const checkOperationsAvailable = (
      operation: number | boolean | string | undefined,
      fieldName?: string,
    ) => {
      if (fieldName === 'agentPolygonCpf') {
        const operationsAvailable = [{ label: 'É igual a', value: { key: '=', kind: 'operator' } }]
        setSelectExpressionOptions(operationsAvailable)
        setSelectCurrentProperty(
          prev => [...prev, { operator: operationsAvailable }] as Expression[],
        )
        return operationsAvailable
      }
      const operationsAvailable =
        ExpressionOperationAvailable[operation as keyof typeof ExpressionOperationAvailable]
      setSelectExpressionOptions(operationsAvailable)
      setSelectCurrentProperty(prev => [...prev, { operator: operationsAvailable }] as Expression[])
      return operationsAvailable
    }

    const getBooleanLabels = () => {
      const options = [
        {
          value: { key: 'true', kind: 'boolean', type: 'boolean' },
          label: 'Verdadeiro',
        },
        {
          value: { key: 'false', kind: 'boolean', type: 'boolean' },
          label: 'Falso',
        },
      ]
      return options
    }

    const getEnumeratedLabels = (currentExpressionSelected: Expression) => {
      if (currentExpressionSelected.kind !== 'binary') return
      const leftExpression = currentExpressionSelected.left as Expression<'property'>
      if (leftExpression.kind !== 'property') return
      const filteredProperty = possibleProperties.filter(property => {
        return property.value.key === leftExpression.property
      })
      const optionsInEnumerated = filteredProperty[0]?.value?.keysAndLabels
      const options = Object.entries(optionsInEnumerated || {}).map(([key, label]) => ({
        value: { key, kind: 'enumerated', type: 'string' },
        label,
      }))
      setSelectExpressionOptions(options)
    }

    const deleteExpression = () => {
      setExpression({} as Expression)
      setSelectCurrentProperty([])
      setNewExpression({ kind: 'binary' } as Expression, Number(expressionIndex))
      removeExpression(expressionIndex)
    }

    const renderSelectOrInput = () => {
      return (
        <ItemsBox>
          {selectCurrentProperty.map((exp, index) => {
            if (exp?.kind === 'binary' && exp?.right?.kind === 'literal' && !exp?.right?.subType) {
              return (
                <InputItemFilter
                  key={index}
                  expression={expression}
                  addToExpression={(element: FilteredType) =>
                    handleNewElementoToExpression(element)
                  }
                  value={exp?.right?.label ?? (exp?.right?.value as string)}
                />
              )
            } else if (exp?.kind === 'binary' && exp?.right?.kind === 'binary') {
              return (
                <TwoInputsBox key={index}>
                  <InputItemFilter
                    expression={expression}
                    addToExpression={(element: FilteredType, isBetween?: boolean) =>
                      handleNewElementoToExpression(element, isBetween)
                    }
                    value={
                      exp?.right?.left?.kind === 'literal'
                        ? (exp?.right?.left?.value as string)
                        : undefined
                    }
                    sideOfExpression='left'
                  />
                  <span> e </span>
                  <InputItemFilter
                    expression={expression}
                    addToExpression={(element: FilteredType, isBetween?: boolean) =>
                      handleNewElementoToExpression(element, isBetween)
                    }
                    value={
                      exp.right?.right?.kind === 'literal'
                        ? (exp?.right?.right?.value as string)
                        : undefined
                    }
                    sideOfExpression='right'
                  />
                </TwoInputsBox>
              )
            } else if (
              exp?.kind === 'binary' &&
              exp?.right?.kind === 'literal' &&
              exp?.right?.subType === 'agentSelect'
            ) {
              return (
                <SelectAgentFilter
                  key={index}
                  placeholder='Buscar'
                  withServiceAreaOnly={
                    expression.kind === 'binary' &&
                    expression.left.kind === 'property' &&
                    expression.left.property === 'agentPolygonCpf'
                  }
                  onSelectAgent={(value: any) => {
                    const element: FilteredType = {
                      value: {
                        key: value?.agentCpf,
                        kind: 'literal',
                        type: 'string',
                        value: value?.agentCpf,
                        label: value?.name,
                      },
                      label: value?.name,
                    }
                    handleNewElementoToExpression(element)
                  }}
                />
              )
            } else if (
              exp?.kind === 'binary' &&
              exp?.right?.kind === 'literal' &&
              exp?.right?.subType === 'regionalSelect'
            ) {
              return (
                <SelectRegionalFilter
                  key={index}
                  placeholder='Buscar'
                  onSelectRegional={(value: { regional: string } | null) => {
                    const element: FilteredType = {
                      value: {
                        key: value?.regional,
                        kind: 'literal',
                        type: 'string',
                        value: value?.regional,
                        label: value?.regional,
                      },
                      label: value?.regional,
                    }
                    handleNewElementoToExpression(element)
                  }}
                />
              )
            } else {
              return (
                <SelectItemFilter
                  key={index}
                  options={selectExpressionOptions ?? []}
                  placeholder='Selecione'
                  isLoading={isLoading}
                  addToExpression={(element: FilteredType) =>
                    handleNewElementoToExpression(element)
                  }
                  value={labels[index]}
                />
              )
            }
          })}
        </ItemsBox>
      )
    }

    return (
      <Container>
        <FiltersBox>
          <div>{renderSelectOrInput()}</div>
          {numberOfOptions > 1 && (
            <WrapperDeleteButton>
              <button onClick={deleteExpression}>
                <FontAwesomeIcon icon={faTrash} color='#414141' />
              </button>
            </WrapperDeleteButton>
          )}
        </FiltersBox>
      </Container>
    )
  },
)

export default FiltersComponent
